import { z } from 'zod';
import { HttpsUrlSchema } from './organization.ts';

/**
 * Source-controlled event configuration — `config/events/*.json`.
 *
 * The API Worker loads this at module import so the public check-in hot path
 * can validate event/location without a D1 read (AGENTS.md invariant 1).
 * D1 holds the same snapshot for foreign keys and admin joins.
 */

/** Matches the CHECK constraint on `events.status` in migration 0001. */
export const EVENT_STATUSES = ['draft', 'scheduled', 'active', 'closed', 'archived'] as const;

/** The subset docs/02_OPENAPI.yaml exposes on `PublicEvent.status`. */
export const PUBLIC_EVENT_STATUSES = ['scheduled', 'active', 'closed'] as const;

export const EventStatusSchema = z.enum(EVENT_STATUSES);
export const PublicEventStatusSchema = z.enum(PUBLIC_EVENT_STATUSES);

export const SlugSchema = z
  .string()
  .max(120)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'must be a lowercase hyphenated slug');

export const FORM_FIELD_TYPES = ['text', 'tel', 'email'] as const;
export const FormFieldTypeSchema = z.enum(FORM_FIELD_TYPES);

/**
 * `additionalProperties: true` in the OpenAPI FormField, so unknown keys pass
 * through to the client instead of failing the build.
 */
export const FormFieldSchema = z.looseObject({
  key: z.string().min(1).max(64),
  label: z.string().min(1),
  type: FormFieldTypeSchema,
  required: z.boolean(),
  max_length: z.int().positive().optional(),
  autocomplete: z.string().optional(),
  help_text: z.string().optional(),
});

export const BrandingSchema = z.looseObject({
  brand_color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'must be a #rrggbb hex color')
    .optional(),
  logo_url: z.string().min(1).optional(),
  /** §28.2: absent banner renders nothing at all — no empty frame. */
  banner_url: z.string().min(1).optional(),
  banner_alt: z.string().optional(),
});

/**
 * Optional per-event check-out.
 *
 * `enabled` is required and separate from the strings so that "an organizer
 * once wrote copy here" can never be mistaken for "check-out is on". The three
 * strings are optional because the participant copy file carries defaults; an
 * organizer who only ticks the box still gets sensible wording.
 *
 * Strict, unlike `BrandingSchema` and `FormFieldSchema`. Those two mirror
 * `additionalProperties: true` in the OpenAPI contract so source-controlled
 * config files carrying extra keys do not fail a build. This object has one
 * writer, the admin UI, and four fixed keys, so there is no forward
 * compatibility to buy — and leniency would cost a silent failure: an
 * organizer who types `buttonLabel` would get a snapshot that publishes
 * cleanly, forwards the stray key into the public bootstrap response, and
 * renders a button that never appears. Adding a key here later still accepts
 * every snapshot written before it, because strict rejects keys the schema
 * does not know, not keys a stored value lacks.
 */
export const EventCheckoutSchema = z.strictObject({
  enabled: z.boolean(),
  notice: z.string().max(300).optional(),
  button_label: z.string().max(60).optional(),
  keep_open_hint: z.string().max(300).optional(),
});

export const EventConfigSchema = z.strictObject({
  id: z.string().min(1).max(100),
  slug: SlugSchema,
  name: z.string().min(1),
  status: EventStatusSchema,
  timezone: z.string().min(1),
  starts_at: z.iso.datetime({ offset: true }).nullish(),
  ends_at: z.iso.datetime({ offset: true }).nullish(),
  config_version: z.int().min(1),
  branding: BrandingSchema.optional(),
  checkout: EventCheckoutSchema.optional(),
  /** Back-link to the main event page on iRace (ticketing / registration). */
  event_url: HttpsUrlSchema.nullish(),
  form: z.strictObject({
    fields: z.array(FormFieldSchema).min(1),
  }),
  messages: z.record(z.string(), z.unknown()).optional(),
});

export const LocationConfigSchema = z.strictObject({
  id: z.string().min(1).max(100),
  code: z.string().min(1).max(32),
  name: z.string().min(1),
  province: z.string().nullish(),
  aliases: z.array(z.string()).default([]),
  sort_order: z.int(),
  is_active: z.boolean(),
  /**
   * A placeholder exists only so `checkins.location_id` can stay NOT NULL for
   * an event whose venue is implicit. It is never shown to a participant.
   */
  is_placeholder: z.boolean().default(false),
});

export const LocationsConfigSchema = z.strictObject({
  event_id: z.string().min(1).max(100),
  /**
   * May be empty. An event held at one known venue should not make someone
   * confirm the only answer before they can type their name; the placeholder
   * row that satisfies the foreign key is created server-side.
   */
  locations: z.array(LocationConfigSchema),
});

export type EventStatus = z.infer<typeof EventStatusSchema>;
export type PublicEventStatus = z.infer<typeof PublicEventStatusSchema>;
export type FormField = z.infer<typeof FormFieldSchema>;
export type Branding = z.infer<typeof BrandingSchema>;
export type EventCheckout = z.infer<typeof EventCheckoutSchema>;
export type EventConfig = z.infer<typeof EventConfigSchema>;
export type LocationConfig = z.infer<typeof LocationConfigSchema>;
export type LocationsConfig = z.infer<typeof LocationsConfigSchema>;
